import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import type { MarketingActivity, ActivityStatus, ActivityType, PriorityLevel, User, Team } from '@/types';
import type { ApiResponse } from '@/types';
import { api } from '@/lib/api';
import { Loader2, Plus, Sparkles, Target, X } from 'lucide-react';
import { useStrategyStore } from '@/stores/strategy';
import type { AISuggestActivityResult } from '@/types';

const STATUS_OPTIONS: { value: ActivityStatus; label: string }[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'todo', label: 'To-Do' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'review', label: 'Review' },
  { value: 'done', label: 'Erledigt' },
];

const TYPE_OPTIONS: { value: ActivityType; label: string }[] = [
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'linkedin_ads', label: 'LinkedIn Ads' },
  { value: 'print', label: 'Print' },
  { value: 'email', label: 'E-Mail' },
  { value: 'campaign', label: 'Kampagne' },
  { value: 'social', label: 'Social' },
  { value: 'event', label: 'Event' },
  { value: 'content', label: 'Content' },
  { value: 'seo', label: 'SEO' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'other', label: 'Sonstige' },
];

const PRIORITY_OPTIONS: { value: PriorityLevel; label: string }[] = [
  { value: 'low', label: 'Niedrig' },
  { value: 'medium', label: 'Mittel' },
  { value: 'high', label: 'Hoch' },
  { value: 'critical', label: 'Kritisch' },
];

export type CreateActivityFormState = {
  plan_id: string;
  title: string;
  description: string;
  type: ActivityType;
  status: ActivityStatus;
  priority: PriorityLevel;
  start_date: string;
  end_date: string;
  budget_planned: string;
  hours_planned: string;
  assigned_to: string | null;
  team_id: string | null;
};

const emptyForm = (planId: string): CreateActivityFormState => ({
  plan_id: planId,
  title: '',
  description: '',
  type: 'other',
  status: 'backlog',
  priority: 'medium',
  start_date: '',
  end_date: '',
  budget_planned: '0',
  hours_planned: '0',
  assigned_to: null,
  team_id: null,
});

export interface CreateActivityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful create */
  onCreated?: () => void | Promise<void>;
  /**
   * When set, the new activity is linked to this campaign and uses its plan.
   * The marketing plan field is fixed for this context.
   */
  campaignContext?: {
    id: string;
    planId: string;
    campaignName: string;
  };
  /**
   * Merged into the form when the dialog opens (e.g. calendar slot pick).
   */
  initialOverrides?: Partial<CreateActivityFormState> | null;
}

export function CreateActivityDialog({
  open,
  onOpenChange,
  onCreated,
  campaignContext,
  initialOverrides,
}: CreateActivityDialogProps) {
  const [planOptions, setPlanOptions] = useState<{ id: string; title: string }[]>([]);
  const [userOptions, setUserOptions] = useState<User[]>([]);
  const [teamOptions, setTeamOptions] = useState<Team[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const { openPanel, strategy } = useStrategyStore();
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateActivityFormState>(() => emptyForm(''));

  const resetCreateForm = useCallback(() => {
    setCreateError(null);
    const basePlan =
      campaignContext?.planId || planOptions[0]?.id || '';
    setCreateForm(emptyForm(basePlan));
  }, [campaignContext?.planId, planOptions]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [plansRes, usersRes, teamsRes] = await Promise.all([
          api.get<ApiResponse<{ id: string; title: string }[]>>('/marketing-plans?limit=100'),
          api.get<ApiResponse<User[]>>('/users?limit=200&is_active=true'),
          api.get<ApiResponse<Team[]>>('/teams?limit=200'),
        ]);
        const plans = Array.isArray(plansRes.data) ? plansRes.data : [];
        setPlanOptions(plans);
        setUserOptions(Array.isArray(usersRes.data) ? usersRes.data : []);
        setTeamOptions(Array.isArray(teamsRes.data) ? teamsRes.data : []);
        setCreateForm((prev) => {
          if (prev.plan_id) return prev;
          const first = plans[0]?.id ?? '';
          return { ...prev, plan_id: first };
        });
      } catch {
        setPlanOptions([]);
      }
    };
    void loadMeta();
  }, []);

  useEffect(() => {
    if (!open) return;
    if (initialOverrides && Object.keys(initialOverrides).length > 0) {
      setCreateForm((prev) => ({ ...prev, ...initialOverrides }));
      return;
    }
    if (campaignContext) {
      setCreateForm(emptyForm(campaignContext.planId));
    }
  }, [open, initialOverrides, campaignContext]);

  const handleAISuggest = async () => {
    setIsSuggesting(true);
    try {
      const res = await api.post<ApiResponse<AISuggestActivityResult>>('/activities/ai-suggest', {
        plan_id: createForm.plan_id || undefined,
        strategy_id: strategy?.id,
      });
      if (res.data) {
        setCreateForm((prev) => ({
          ...prev,
          title: res.data!.title || prev.title,
          description: res.data!.description || prev.description,
          type: (res.data!.type as ActivityType) || prev.type,
          priority: (res.data!.priority as PriorityLevel) || prev.priority,
          budget_planned:
            res.data!.budget_planned > 0 ? String(res.data!.budget_planned) : prev.budget_planned,
        }));
      }
    } catch {
      // silently fail – user can still fill manually
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleCreateActivity = async () => {
    setCreateError(null);
    if (!createForm.plan_id) {
      setCreateError('Bitte zuerst einen Marketingplan wählen.');
      return;
    }
    if (!createForm.title.trim()) {
      setCreateError('Bitte einen Titel für die Aktivität eingeben.');
      return;
    }
    if (createForm.end_date && createForm.start_date && createForm.end_date < createForm.start_date) {
      setCreateError('Das Enddatum muss nach dem Startdatum liegen.');
      return;
    }

    const budgetPlanned = Number(createForm.budget_planned);
    const hoursPlanned = Number(createForm.hours_planned);
    if (Number.isNaN(budgetPlanned) || budgetPlanned < 0 || Number.isNaN(hoursPlanned) || hoursPlanned < 0) {
      setCreateError('Budget und Stunden müssen gültige positive Werte sein.');
      return;
    }

    setCreating(true);
    try {
      await api.post<ApiResponse<MarketingActivity>>('/activities', {
        plan_id: createForm.plan_id,
        campaign_id: campaignContext?.id ?? null,
        title: createForm.title.trim(),
        description: createForm.description.trim() || null,
        description_md: createForm.description.trim() || null,
        type: createForm.type,
        status: createForm.status,
        priority: createForm.priority,
        start_date: createForm.start_date || null,
        end_date: createForm.end_date || null,
        budget_planned: budgetPlanned,
        hours_planned: hoursPlanned,
        assigned_to: createForm.assigned_to || null,
        team_id: createForm.team_id || null,
      });
      onOpenChange(false);
      resetCreateForm();
      await onCreated?.();
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Aktivität konnte nicht erstellt werden.');
    } finally {
      setCreating(false);
    }
  };

  const planTitle =
    planOptions.find((p) => p.id === createForm.plan_id)?.title ?? 'Marketingplan';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          resetCreateForm();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle>Neue Aktivität</DialogTitle>
            <button
              type="button"
              onClick={() => void openPanel()}
              className="flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium text-foreground hover:bg-card-hover dark:text-muted dark:hover:bg-primary-hover/30"
              title="Marketingstrategie anzeigen"
            >
              <Target size={12} />
              Strategie {strategy ? strategy.year : new Date().getFullYear()}
            </button>
          </div>
          <DialogDescription>
            {campaignContext ? (
              <>
                Wird der Kampagne «{campaignContext.campaignName}» zugeordnet ({planTitle}).
              </>
            ) : (
              <>Aktivität inkl. Status, Priorität und Planung anlegen.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          {createError && (
            <p className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
              {createError}
            </p>
          )}
          <div className="grid gap-2">
            <Label>Marketingplan</Label>
            {campaignContext ? (
              <p className="rounded-md border border-border bg-background px-3 py-2 text-sm text-muted dark:bg-card/60 dark:text-foreground">
                {planTitle}
              </p>
            ) : (
              <Select
                value={createForm.plan_id}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, plan_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Plan wählen" />
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label>Titel</Label>
              <button
                type="button"
                onClick={() => void handleAISuggest()}
                disabled={isSuggesting}
                className="flex items-center gap-1 text-xs font-medium text-foreground disabled:opacity-50 dark:text-muted"
              >
                {isSuggesting ? (
                  <>
                    <Loader2 size={11} className="animate-spin" /> Generiere…
                  </>
                ) : (
                  <>
                    <Sparkles size={11} /> KI-Vorschlag aus Strategie
                  </>
                )}
              </button>
            </div>
            <AIInput
              value={createForm.title}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
              onValueChange={(v) => setCreateForm((prev) => ({ ...prev, title: v }))}
              placeholder="z. B. LinkedIn Kampagne Q2"
              aiFieldLabel="Aktivitäts-Titel"
              aiContext={`Marketing-Aktivität, Typ: ${createForm.type}`}
            />
          </div>
          <div className="grid gap-2">
            <Label>Beschreibung</Label>
            <AIInput
              value={createForm.description}
              onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
              onValueChange={(v) => setCreateForm((prev) => ({ ...prev, description: v }))}
              placeholder="Kurze Beschreibung"
              aiFieldLabel="Aktivitäts-Beschreibung"
              aiContext={`Aktivität: ${createForm.title || 'neue Aktivität'}, Typ: ${createForm.type}`}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-2">
              <Label>Typ</Label>
              <Select
                value={createForm.type}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, type: value as ActivityType }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Status</Label>
              <Select
                value={createForm.status}
                onValueChange={(value) => setCreateForm((prev) => ({ ...prev, status: value as ActivityStatus }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Priorität</Label>
              <Select
                value={createForm.priority}
                onValueChange={(value) =>
                  setCreateForm((prev) => ({ ...prev, priority: value as PriorityLevel }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(userOptions.length > 0 || teamOptions.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {userOptions.length > 0 && (
                <div className="grid gap-2">
                  <Label>Zugewiesen an</Label>
                  <Select
                    value={createForm.assigned_to ?? 'none'}
                    onValueChange={(v) =>
                      setCreateForm((prev) => ({ ...prev, assigned_to: v === 'none' ? null : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Niemand" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Niemand</SelectItem>
                      {userOptions.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {teamOptions.length > 0 && (
                <div className="grid gap-2">
                  <Label>Team</Label>
                  <Select
                    value={createForm.team_id ?? 'none'}
                    onValueChange={(v) =>
                      setCreateForm((prev) => ({ ...prev, team_id: v === 'none' ? null : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kein Team" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Team</SelectItem>
                      {teamOptions.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Startdatum</Label>
              <Input
                type="date"
                value={createForm.start_date}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Enddatum</Label>
              <Input
                type="date"
                value={createForm.end_date}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Geplantes Budget</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={createForm.budget_planned}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, budget_planned: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Geplante Stunden</Label>
              <Input
                type="number"
                min="0"
                step="0.25"
                value={createForm.hours_planned}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, hours_planned: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetCreateForm();
            }}
          >
            <X className="h-4 w-4" />
            Abbrechen
          </Button>
          <Button onClick={() => void handleCreateActivity()} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Erstellen…
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                Erstellen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
